import React from 'react';
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import GameActionBar from '../features/scrabble/GameActionBar';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import ScrabbleBoard from '../features/scrabble/ScrabbleBoard';
import TileRack from '../features/scrabble/TileRack';
import ScorePanel from '../features/scrabble/ScorePanel';
import LetterTile from '../features/scrabble/LetterTile';
import ShopOverlay, { ShopActionButtons } from '../features/scrabble/ShopOverlay';
import ModifierPanel from '../features/scrabble/ModifierPanel';
import VictoryOverlay from '../features/scrabble/VictoryOverlay';
import GameOverOverlay from '../features/scrabble/GameOverOverlay';
import PageTitle from '../components/PageTitle';
import { getTileModifierMeta, getModifierMeta } from '../features/scrabble/boardConfig';
import { LETTER_POINTS } from '../features/scrabble/config/gameConfig';
import { useScrabbleGame } from '../features/scrabble/hooks/useScrabbleGame';
import { ScrabbleGameProvider } from '../features/scrabble/context/ScrabbleGameContext';

function PlayPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const modifierOverrides = {};

  const game = useScrabbleGame({ modifierOverrides });

  const {
    boardTiles,
    committedBoard,
    stagedBoard,
    rackTiles,
    gamePhase,
    pendingBoardPlacement,
    isDiscardSelecting,
    discardSelectionTileIds,
    tileValidationState,
    placeRackTile,
    reorderRackTile,
    moveStagedTile,
    returnStagedTileToRack,
    toggleDiscardTileSelection,
    startBoardModifierPlacement,
    placeBoardModifier,
    cancelBoardPlacement,
    forceEndRun,
  } = game;

  const [activeTile, setActiveTile] = React.useState(null);
  const [hoveredSquareId, setHoveredSquareId] = React.useState(null);
  const [inspectedRackTile, setInspectedRackTile] = React.useState(null);
  const [isBoardVisibleOnMobile, setIsBoardVisibleOnMobile] = React.useState(true);
  const [isInventoryOpen, setIsInventoryOpen] = React.useState(false);
  const [recentlyPlacedSquareId, setRecentlyPlacedSquareId] = React.useState(null);
  const hoveredSquareIdRef = React.useRef(null);
  const playScrollRef = React.useRef(null);
  const boardContainerRef = React.useRef(null);
  const recentlyPlacedTimeoutRef = React.useRef(null);

  const setHoveredSquare = React.useCallback((nextHoveredSquareId) => {
    if (hoveredSquareIdRef.current === nextHoveredSquareId) {
      return;
    }

    hoveredSquareIdRef.current = nextHoveredSquareId;
    setHoveredSquareId(nextHoveredSquareId);
  }, []);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 2 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const dragOverlayDropAnimation = React.useMemo(
    () => ({ duration: 120, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }),
    []
  );

  const handleInspectTile = React.useCallback((tile) => {
    setInspectedRackTile(tile || null);
  }, []);

  const inspectedTileBaseScore = React.useMemo(
    () => LETTER_POINTS[String(inspectedRackTile?.letter || '').toUpperCase()] || 0,
    [inspectedRackTile]
  );

  const inspectedTileScoreBonus = React.useMemo(() => {
    const mods = inspectedRackTile?.modifiers;
    if (!mods || typeof mods !== 'object' || Array.isArray(mods)) return 0;
    return Number(mods.score) || 0;
  }, [inspectedRackTile]);

  const inspectedTileModifiers = React.useMemo(() => {
    const mods = inspectedRackTile?.modifiers;
    if (!mods || typeof mods !== 'object' || Array.isArray(mods)) return [];

    const result = [];

    if (Number(mods.score) > 0) {
      result.push({
        id: 'score-bonus',
        label: `+${Number(mods.score)}`,
        description: `Adds ${Number(mods.score)} to this tile's score.`,
      });
    }

    if (Number(mods.multiplier) > 1) {
      result.push({
        id: 'tile-multiplier',
        label: `x${Number(mods.multiplier)} word`,
        description: `Multiplies the word score by ${Number(mods.multiplier)}.`,
      });
    }

    const extras = Array.isArray(mods.extras) ? mods.extras : [];
    extras.forEach((modifierId) => {
      if (!modifierId) return;
      const modifierMeta = getTileModifierMeta(modifierId);
      result.push({
        id: modifierId,
        label: modifierMeta.label,
        description: modifierMeta.description,
      });
    });

    return result;
  }, [inspectedRackTile]);

  const inspectedTileDisplayScore = React.useMemo(
    () => Math.max(0, Math.ceil(inspectedTileBaseScore + inspectedTileScoreBonus)),
    [inspectedTileBaseScore, inspectedTileScoreBonus]
  );

  const inspectedBoardSquareModifiers = React.useMemo(() => {
    if (!inspectedRackTile?.boardSquareModifiers?.length) {
      return [];
    }

    return inspectedRackTile.boardSquareModifiers;
  }, [inspectedRackTile]);

  const handleDragStart = React.useCallback((event) => {
    if (isDiscardSelecting) {
      return;
    }

    const tile = event?.active?.data?.current?.tile;
    setActiveTile(tile || null);
    setHoveredSquare(null);
    setInspectedRackTile(null);
  }, [setHoveredSquare, isDiscardSelecting]);

  const handleDragOver = React.useCallback((event) => {
    if (isDiscardSelecting) {
      return;
    }

    const overData = event?.over?.data?.current;
    if (overData?.type === 'square') {
      setHoveredSquare(overData.tileId);
      return;
    }

    setHoveredSquare(null);
  }, [setHoveredSquare, isDiscardSelecting]);

  const handleDragEnd = React.useCallback((event) => {
    if (isDiscardSelecting) {
      setActiveTile(null);
      setHoveredSquare(null);
      return;
    }

    const { active, over } = event;
    setActiveTile(null);
    setHoveredSquare(null);

    if (!active || !over) {
      return;
    }

    const activeData = active.data.current;
    const overData = over.data.current;

    if (!activeData || !overData) {
      return;
    }

    const dragSource = activeData.from || activeData.source;
    const sourceTileId = activeData.tileId || activeData.fromTileId;

    if (dragSource === 'rack' && overData.type === 'square') {
      placeRackTile(activeData.tile.id, overData.tileId);
      return;
    }

    if (dragSource === 'rack' && overData.type === 'rack-slot') {
      reorderRackTile(activeData.tile.id, overData.index);
      return;
    }

    if (dragSource === 'board' && overData.type === 'square' && sourceTileId) {
      moveStagedTile(sourceTileId, overData.tileId);
      return;
    }

    if (
      dragSource === 'board' &&
      (overData.type === 'rack' || overData.type === 'rack-drop' || overData.type === 'rack-slot') &&
      sourceTileId
    ) {
      returnStagedTileToRack(sourceTileId);
    }
  }, [
    placeRackTile,
    reorderRackTile,
    moveStagedTile,
    returnStagedTileToRack,
    setHoveredSquare,
    isDiscardSelecting,
  ]);

  const handleDragCancel = React.useCallback(() => {
    setActiveTile(null);
    setHoveredSquare(null);
  }, [setHoveredSquare]);

  React.useEffect(() => {
    if (!isMobile) {
      setIsBoardVisibleOnMobile(true);
      return undefined;
    }

    const root = playScrollRef.current;
    const target = boardContainerRef.current;

    if (!root || !target) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsBoardVisibleOnMobile(Boolean(entry?.isIntersecting && entry.intersectionRatio > 0.05));
      },
      { root, threshold: [0, 0.05, 0.2] }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [isMobile]);

  const boardFrameWidth = {
    xs: 'min(100%, 640px)',
    lg: 'min(76vw, calc(100dvh - 250px))',
  };

  const handleToggleInventory = React.useCallback(() => {
    setIsInventoryOpen((prev) => !prev);
  }, []);

  const handlePlaceModifier = React.useCallback((heldId) => {
    startBoardModifierPlacement(heldId);
    setIsInventoryOpen(false);
  }, [startBoardModifierPlacement]);

  const handlePlaceBoardModifier = React.useCallback((squareTileId) => {
    const ok = placeBoardModifier(squareTileId);
    if (ok) {
      clearTimeout(recentlyPlacedTimeoutRef.current);
      setRecentlyPlacedSquareId(squareTileId);
      recentlyPlacedTimeoutRef.current = setTimeout(() => setRecentlyPlacedSquareId(null), 650);
    }
  }, [placeBoardModifier]);

  React.useEffect(() => {
    const t = recentlyPlacedTimeoutRef.current;
    return () => clearTimeout(t);
  }, []);

  React.useEffect(() => {
    if (gamePhase === 'victory' && isInventoryOpen) {
      setIsInventoryOpen(false);
    }
  }, [gamePhase, isInventoryOpen]);

  React.useEffect(() => {
    const handleEndRunRequest = () => forceEndRun();
    window.addEventListener('altessera:request-end-run', handleEndRunRequest);
    return () => window.removeEventListener('altessera:request-end-run', handleEndRunRequest);
  }, [forceEndRun]);

  return (
    <ScrabbleGameProvider value={game}>
      <Box
        ref={playScrollRef}
        sx={{
          height: { xs: 'calc(100svh - 56px)', lg: '100dvh' },
          minHeight: { xs: 'calc(100svh - 56px)', lg: '100dvh' },
          boxSizing: 'border-box',
          overflowY: { xs: 'auto', lg: 'hidden' },
          overscrollBehaviorY: 'contain',
          WebkitOverflowScrolling: 'touch',
          px: { xs: 1, sm: 2, lg: 3 },
          py: { xs: 1.1, sm: 1.2, lg: 1.8 },
          pb: { xs: '170px', lg: 1.8 },
          background: 'linear-gradient(180deg, #f8fbff 0%, #f5f7fb 100%)',
        }}
      >
        <Stack
          spacing={{ xs: 1, sm: 0.9, md: 1.1 }}
          sx={{ maxWidth: 1220, mx: 'auto', height: { xs: 'auto', lg: '100%' } }}
        >
          <PageTitle />

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={{ xs: 0.9, lg: 1.6 }}
            sx={{
              width: '100%',
              minHeight: 0,
              flex: { xs: 'unset', lg: 1 },
              alignItems: 'stretch',
              justifyContent: { xs: 'flex-start', lg: 'center' },
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'center',
              }}
            >
              {pendingBoardPlacement ? (
                <Box
                  sx={{
                    width: boardFrameWidth,
                    maxWidth: 980,
                    minHeight: 0,
                    height: { xs: 'auto', lg: '100%' },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: (theme) => `1px solid ${theme.palette.success.light}`,
                      backgroundColor: (theme) => `${theme.palette.success.light}22`,
                      px: 1.5,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      '@keyframes bannerSlideIn': {
                        from: { opacity: 0, transform: 'translateY(-6px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      animation: 'bannerSlideIn 220ms cubic-bezier(0.22, 1, 0.36, 1)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                      {(() => {
                        const meta = pendingBoardPlacement.boardSquareModifierId
                          ? getModifierMeta(pendingBoardPlacement.boardSquareModifierId)
                          : null;
                        return meta ? (
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1,
                              flexShrink: 0,
                              backgroundColor: (theme) =>
                                theme.palette.scrabble?.[meta.colorKey] || theme.palette.action.selected,
                              border: (theme) => `1px solid ${theme.palette.divider}`,
                              display: 'grid',
                              placeItems: 'center',
                            }}
                          >
                            <Typography sx={{ fontWeight: 900, fontSize: 10, userSelect: 'none', letterSpacing: 0.3 }}>
                              {meta.label}
                            </Typography>
                          </Box>
                        ) : null;
                      })()}
                      <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
                        Click an empty square to place:{' '}
                        <Box component="span" sx={{ color: 'success.dark' }}>
                          {pendingBoardPlacement.modifierLabel}
                        </Box>
                      </Typography>
                    </Box>
                    <Button variant="outlined" size="small" onClick={cancelBoardPlacement}>
                      Cancel
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      flex: { xs: 'unset', lg: 1 },
                      minHeight: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DndContext>
                      <ScrabbleBoard
                        boardTiles={boardTiles}
                        committedBoard={committedBoard}
                        stagedBoard={stagedBoard}
                        tileValidationState={{}}
                        hoveredSquareId={null}
                        onInspectTile={handleInspectTile}
                        placementMode
                        onSquareClick={handlePlaceBoardModifier}
                      />
                    </DndContext>
                  </Box>
                </Box>
              ) : isInventoryOpen ? (
                <Box
                  sx={{
                    width: boardFrameWidth,
                    maxWidth: 980,
                    alignSelf: { xs: 'flex-start', lg: 'stretch' },
                    minHeight: 0,
                    height: { xs: 'auto', lg: '100%' },
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <ModifierPanel onPlaceModifier={handlePlaceModifier} onInspectTile={handleInspectTile} />
                </Box>
              ) : gamePhase === 'game-over' ? (
                <Box
                  sx={{
                    width: boardFrameWidth,
                    maxWidth: 980,
                    alignSelf: { xs: 'flex-start', lg: 'center' },
                  }}
                >
                  <GameOverOverlay />
                </Box>
              ) : gamePhase === 'victory' ? (
                <Box
                  sx={{
                    width: boardFrameWidth,
                    maxWidth: 980,
                    alignSelf: { xs: 'flex-start', lg: 'center' },
                  }}
                >
                  <VictoryOverlay />
                </Box>
              ) : gamePhase === 'shop' ? (
                <Box
                  sx={{
                    width: boardFrameWidth,
                    maxWidth: 980,
                    alignSelf: { xs: 'flex-start', lg: 'center' },
                  }}
                >
                  <ShopOverlay onInspectTile={handleInspectTile} />
                </Box>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={pointerWithin}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDragCancel={handleDragCancel}
                >
                  <Box
                    sx={{
                      width: boardFrameWidth,
                      maxWidth: 980,
                      minHeight: 0,
                      height: { xs: 'auto', lg: '100%' },
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      ref={boardContainerRef}
                      sx={{
                        flex: { xs: 'unset', lg: 1 },
                        minHeight: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ScrabbleBoard
                        boardTiles={boardTiles}
                        committedBoard={committedBoard}
                        stagedBoard={stagedBoard}
                        tileValidationState={tileValidationState}
                        hoveredSquareId={hoveredSquareId}
                        onInspectTile={handleInspectTile}
                        recentlyPlacedSquareId={recentlyPlacedSquareId}
                      />
                    </Box>
                    <Box
                      sx={{
                        marginTop: 0.85,
                        marginBottom: { xs: 0.4, sm: 0 },
                        width: { xs: 'calc(100% + 8px)', sm: '100%' },
                        marginLeft: { xs: '-4px', sm: 0 },
                        position: { xs: isBoardVisibleOnMobile ? 'static' : 'sticky', lg: 'static' },
                        top: { xs: 0, lg: 'auto' },
                        zIndex: { xs: 5, lg: 'auto' },
                        backgroundColor: {
                          xs: isBoardVisibleOnMobile ? 'transparent' : 'rgba(248, 251, 255, 0.92)',
                          lg: 'transparent',
                        },
                        backdropFilter: { xs: isBoardVisibleOnMobile ? 'none' : 'blur(2px)', lg: 'none' },
                        paddingTop: { xs: 0.35, sm: 0 },
                      }}
                    >
                      <TileRack
                        tiles={rackTiles}
                        onInspectTile={handleInspectTile}
                        discardMode={gamePhase === 'playing' && isDiscardSelecting}
                        selectedDiscardTileIds={discardSelectionTileIds}
                        onToggleDiscardTile={toggleDiscardTileSelection}
                      />
                    </Box>
                  </Box>
                  <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={dragOverlayDropAnimation}>
                    {activeTile ? (
                      <LetterTile
                        tile={activeTile}
                        draggable={false}
                        compact
                        variant="overlay"
                        isHoveringBoard={Boolean(hoveredSquareId)}
                      />
                    ) : null}
                  </DragOverlay>
                </DndContext>
              )}
            </Box>

            <Box
              sx={{
                width: { xs: '100%', lg: 300 },
                minHeight: 0,
                maxHeight: { xs: 'none', lg: '100%' },
                display: 'flex',
              }}
            >
              <ScorePanel
                isInventoryOpen={isInventoryOpen}
                onToggleInventory={handleToggleInventory}
                inspectedRackTile={inspectedRackTile}
                inspectedTileModifiers={inspectedTileModifiers}
                inspectedTileBaseScore={inspectedTileBaseScore}
                inspectedTileDisplayScore={inspectedTileDisplayScore}
                inspectedTileScoreBonus={inspectedTileScoreBonus}
                inspectedBoardSquareModifiers={inspectedBoardSquareModifiers}
              />
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          display: { xs: 'block', lg: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: 'background.paper',
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
          px: { xs: 1.5, sm: 2 },
          pt: 1.5,
          pb: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        }}
      >
        <Box sx={{ maxWidth: 1220, mx: 'auto' }}>
          {pendingBoardPlacement ? (
            <Button fullWidth variant="outlined" onClick={cancelBoardPlacement}>
              Cancel Placement
            </Button>
          ) : gamePhase === 'shop' ? (
            <ShopActionButtons
              isInventoryOpen={isInventoryOpen}
              onToggleInventory={handleToggleInventory}
            />
          ) : (
            <GameActionBar
              isInventoryOpen={isInventoryOpen}
              onToggleInventory={handleToggleInventory}
            />
          )}
        </Box>
      </Box>
    </ScrabbleGameProvider>
  );
}

export default PlayPage;
